<template>
  <b-modal
    id="modal-alarm-filters"
    @show="onShow"
    centered
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="lg"
  >
    <div>
      <b-row align-v="center" class="d-flex align-items-center">
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("alarm.status") }}
          </label>
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            v-model="statusFilter"
            :options="[
              { label: $t('alarm.waiting'), value: 'waiting' },
              { label: $t('alarm.attending'), value: 'attending' },
              { label: $t('alarm.canceled'), value: 'canceled' },
              { label: $t('alarm.attended'), value: 'attended' },
            ]"
            class="w-100 text-capitalize"
            multiple
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $tc("tenants.title", 2) }}
          </label>
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            v-model="tenantFilter"
            :options="tenantOptions"
            class="w-100"
            multiple
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $tc("unit.title", 2) }}
          </label>
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            v-model="unitFilter"
            :options="unitOptions"
            class="w-100"
            multiple
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $tc("camera.title", 2) }}
          </label>
          <v-select
            v-model="cameraArrayFilter"
            :options="formattedOptions"
            :filterable="false"
            :clearable="false"
            :reduce="(val) => val.value"
            :calculate-position="withPopper"
            @search="onSearch"
            @option:selected="optionSelect"
            @option:deselected="optionDeselect"
            append-to-body
            multiple
          >
            <template #list-footer>
              <li class="pagination">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="!hasPrevPage"
                  @click="prevPage"
                >
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="!hasNextPage"
                  @click="nextPage"
                >
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </b-button>
              </li>
            </template>
          </v-select>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("alarm.alarm_type") }}
          </label>
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            v-model="alarmTypeFilter"
            :options="alarmTypeOptions"
            class="w-100"
            multiple
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="mb-2" v-if="$can('filter_datetime', 'Alarm')">
          <label class="text-capitalize">
            {{ $t("alarm.start_date_filter") }}
          </label>
          <flat-pickr
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            v-model="startDate"
          />
        </b-col>
        <b-col cols="6" class="mb-2" v-if="$can('filter_datetime', 'Alarm')">
          <label class="text-capitalize">
            {{ $t("alarm.end_date_filter") }}
          </label>
          <flat-pickr
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            v-model="endDate"
          />
        </b-col>
        <b-col cols="6" class="mb-2" v-if="$can('filter_datetime', 'Alarm')">
          <label class="text-capitalize">
            {{ $t("alarm.dateRange") }}
          </label>
          <v-select
            v-model="dateRangeFilter"
            :options="[
              { label: $t('alarm.dateRangeNone'), value: null },
              { label: $t('alarm.dateRangeLast30'), value: 30 },
              { label: $t('alarm.dateRangeLast60'), value: 60 },
            ]"
            :multiple="false"
            :clearable="false"
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
          <small v-if="dateRangeFilter" class="text-muted">
            {{ $t("alarm.dateRangeHint") }}
          </small>
        </b-col>
      </b-row>
    </div>
    <template #modal-footer>
      <b-button
        size="sm"
        variant="secondary"
        class="text-capitalize"
        @click="$bvModal.hide('modal-alarm-filters')"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        variant="info"
        size="sm"
        class="text-capitalize"
        @click="applyFilters"
      >
        {{ $t("actions.accept") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormCheckbox,
  BBadge,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import popperHelper from "@/libs/popper-helper";
import { debounce } from "lodash";
import axios from "@axios";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BFormCheckbox,
    vSelect,
    BBadge,
    flatPickr,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      statusFilter: [],
      tenantFilter: [],
      unitFilter: [],
      cameraArrayFilter: [],
      alarmTypeFilter: [],
      startDate: "",
      endDate: "",
      // Days back from "now" to compute at alarm-view load time; null = use the manual dates above.
      dateRangeFilter: null,

      currentPage: 1,
      search: null,
      meta: null,
      limit: 8,
      options: [],
      camerasProxy: [],
    };
  },
  computed: {
    unitOptions() {
      return this.$store.getters['unit/getSelectedItems'];
    },
    tenantOptions() {
      return this.$store.getters['tenant/getSelectedItems'];
    },
    alarmTypeOptions() {
      return this.$store.getters['type_alarm/getSelectedItems'];
    },
    filtersText() {
      const parts = [];
      if (this.statusFilter?.length){
        parts.push(this.$t("alarm.status"));
      }
      if (this.tenantFilter?.length){
        parts.push(this.$tc("tenants.title", 2));
      }
      if (this.unitFilter?.length){
        parts.push(this.$tc("unit.title", 2));
      }
      if (this.cameraArrayFilter?.length){
        parts.push(this.$tc("camera.title", 2));
      }
      if (this.alarmTypeFilter?.length){
        parts.push(this.$t("alarm.alarm_type"));
      }
      if (this.dateRangeFilter) {
        parts.push(
          this.dateRangeFilter === 30
            ? this.$t("alarm.dateRangeLast30")
            : this.$t("alarm.dateRangeLast60")
        );
      } else {
        if (this.startDate) {
          parts.push(this.$t("alarm.start_date_filter"));
        }
        if (this.endDate){
          parts.push(this.$t("alarm.end_date_filter"));
        }
      }
      return parts.length ? parts.join("<br>") : '';
    },
    countActiveFilters() {
      if (!this.filters) return 0;
      let count = 0;
      for (const key in this.filters) {
        const value = this.filters[key];
        if (Array.isArray(value)) {
          if (value.length > 0) count++;
        } else if (value !== null && value !== "" && value !== undefined) {
          count++;
        }
      }
      return count;
    },
    hasNextPage() {
      if (this.meta) {
        return this.currentPage + 1 <= this.meta;
      }
      return false;
    },

    hasPrevPage() {
      return this.currentPage - 1 > 0;
    },

    formattedOptions() {
      return this.options.map(camera_proxy => ({
        label: camera_proxy.camera.name ?? null,
        value: camera_proxy.id,
      }));
    },
  },
  methods: {
    async onShow() {
      await this.$store.dispatch("unit/getAll");
      await this.$store.dispatch("tenant/getAll");
      await this.$store.dispatch("type_alarm/getAll");

      if (this.filters && Object.keys(this.filters).length) {
        this.statusFilter = this.filters.status || [];
        this.tenantFilter = this.filters.tenant || [];
        this.unitFilter = this.filters.unit || [];
        this.cameraArrayFilter = this.filters.cameraArray || [];
        this.alarmTypeFilter = this.filters.alarmType || [];
        this.startDate = this.filters.startDate || "";
        this.endDate = this.filters.endDate || "";
        this.dateRangeFilter = this.filters.dateRangeFilter || null;
      } else {
        this.statusFilter = [];
        this.tenantFilter = [];
        this.unitFilter = [];
        this.cameraArrayFilter = [];
        this.alarmTypeFilter = [];
        this.startDate = "";
        this.endDate = "";
        this.dateRangeFilter = null;
      }
    },
    applyFilters() {
      const payload = this.updateFilters();
      this.$emit("filters-applied", payload);
      this.$bvModal.hide("modal-alarm-filters");
    },
    updateFilters() {
      const payload = {
        filters: {
          status: this.statusFilter,
          tenant: this.tenantFilter,
          unit: this.unitFilter,
          cameraArray: this.cameraArrayFilter,
          alarmType: this.alarmTypeFilter,
          startDate: this.startDate,
          endDate: this.endDate,
          dateRangeFilter: this.dateRangeFilter,
        },
        filtersText: this.filtersText,
        filtersCount: this.countActiveFilters,
      };
      this.$emit("filters-selected", payload);
      return payload;
    },
    fetchCameraData() {
      const params = {
        q: this.search,
        max: this.limit,
        "page[n]": this.currentPage,
        sortBy: "id",
        sortDesc: true,
      };

      return axios("v1/proxy_camera?deviceType=camera", { params })
        .then(response => {
          this.meta = response.data.meta.last_page;
          this.options = response.data.data;
        })
        .catch(() => {
          console.log("SelectPaginateAjax: Error obteniendo la información");
        });
    },
    onSearch: debounce(function (query, loading) {
      if (query.length) {
        loading(true);
        this.search = query;
        this.fetchCameraData().finally(() => loading(false));
      } else {
        this.currentPage = 1;
        this.search = "";
        this.fetchCameraData();
      }
    }, 500),

    nextPage() {
      this.currentPage += 1;
    },

    prevPage() {
      this.currentPage -= 1;
    },

    optionSelect(selected) {
      const selectedData = selected.map(item => item.value);
      this.$emit("updateCameraFilter", selectedData);
    },

    optionDeselect(deselected) {
      const deselectedData = this.cameraArray.filter(
        item => item.value !== deselected.value
      );
      this.$emit("updateCameraFilter", deselectedData);
    },
  },
  setup() {
    return {
      withPopper: popperHelper.withPopper,
    };
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(newFilters, oldFilters) {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          this.statusFilter = newFilters.status || [];
          this.tenantFilter = newFilters.tenant || [];
          this.unitFilter = newFilters.unit || [];
          this.cameraArrayFilter = newFilters.cameraArray || [];
          this.alarmTypeFilter = newFilters.alarmType || [];
          this.startDate = newFilters.startDate || "";
          this.endDate = newFilters.endDate || "";
          this.dateRangeFilter = newFilters.dateRangeFilter || null;
          this.updateFilters();
        }
      },
    },
    currentPage() {
      this.fetchCameraData();
    },

    cameraFilter(val) {
      if (!val) {
        this.cameraArray = [];
      } else {
        this.cameraArray = [...val];
      }
    },
  },
  mounted() {
    this.fetchCameraData().then(() => {
      if (this.cameraFilter) {
        this.cameraArray = [...this.cameraFilter];
      }
    });
  },
};
</script>
